// tab/src/components/MessageCenter/MessageCenter.tsx
// OnRounds Phase 2 - Message Center Component

import React, { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Card,
  Text,
  Button,
  Badge,
  Avatar,
  Input,
  Textarea,
  Dropdown,
  Option,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Field,
  Tab,
  TabList,
  MessageBar,
  MessageBarBody,
  Spinner,
  Divider,
  Tooltip,
} from '@fluentui/react-components';
import {
  Mail24Regular,
  MailRead24Regular,
  Send24Regular,
  ArrowReply24Regular,
  Flag24Regular,
  Flag24Filled,
  Clock24Regular,
  Person24Regular,
  Checkmark24Regular,
  Warning24Regular,
  ArrowClockwise24Regular,
} from '@fluentui/react-icons';
import { format, formatDistanceToNow } from 'date-fns';
import { useTeam } from '../../context/TeamContext';
import { useUser } from '../../context/UserContext';
import { api } from '../../services/api';
import { Loading } from '../common/Loading';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
    height: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  content: {
    display: 'grid',
    gridTemplateColumns: '350px 1fr',
    ...shorthands.gap('24px'),
    flexGrow: 1,
    minHeight: 0,
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    height: '100%',
    ...shorthands.overflow('hidden'),
  },
  messageList: {
    flexGrow: 1,
    ...shorthands.overflow('auto'),
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
  },
  messageItem: {
    ...shorthands.padding('12px'),
    ...shorthands.borderRadius('8px'),
    cursor: 'pointer',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  messageItemSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.borderColor(tokens.colorBrandStroke1),
  },
  messageItemUnread: {
    fontWeight: tokens.fontWeightSemibold,
    ...shorthands.borderLeft('3px', 'solid', tokens.colorBrandForeground1),
  },
  messageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '4px',
  },
  messageSubject: {
    flexGrow: 1,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  messagePreview: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  messageMeta: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    marginTop: '8px',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  priorityIndicator: {
    width: '8px',
    height: '8px',
    ...shorthands.borderRadius('50%'),
  },
  priorityCritical: {
    backgroundColor: tokens.colorPaletteRedForeground1,
  },
  priorityUrgent: {
    backgroundColor: tokens.colorPaletteYellowForeground1,
  },
  priorityRoutine: {
    backgroundColor: tokens.colorNeutralForeground3,
  },
  detailPanel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    ...shorthands.overflow('hidden'),
  },
  detailCard: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.overflow('hidden'),
  },
  detailHeader: {
    ...shorthands.padding('20px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke1),
  },
  detailBody: {
    flexGrow: 1,
    ...shorthands.padding('20px'),
    ...shorthands.overflow('auto'),
  },
  detailFooter: {
    ...shorthands.padding('16px', '20px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke1),
    display: 'flex',
    ...shorthands.gap('12px'),
  },
  handoverHistory: {
    marginTop: '24px',
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('8px'),
  },
  handoverItem: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
    ...shorthands.padding('8px', '0'),
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: tokens.colorNeutralForeground3,
    ...shorthands.gap('16px'),
  },
  composeForm: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  priorityOption: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
});

interface Message {
  id: string;
  subject: string;
  body: string;
  fromUserId: string;
  fromUserName: string;
  toRoleId: string;
  toRoleName: string;
  priority: 'routine' | 'urgent' | 'critical';
  status: string;
  createdAt: string;
  readAt: string | null;
  handoverHistory: Array<{
    timestamp: string;
    fromUserName: string;
    toUserName: string;
    acknowledged: boolean;
    acknowledgedAt: string | null;
  }>;
}

interface Role {
  id: string;
  name: string;
  status: string;
}

export const MessageCenter: React.FC = () => {
  const styles = useStyles();
  const { currentTeam } = useTeam();
  const { user, currentShift } = useUser();

  const [activeTab, setActiveTab] = useState<'inbox' | 'sent'>('inbox');
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Compose state
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeToRole, setComposeToRole] = useState<string>('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composePriority, setComposePriority] = useState<'routine' | 'urgent' | 'critical'>('routine');
  const [isSending, setIsSending] = useState(false);

  // Load messages
  const loadMessages = useCallback(async () => {
    if (!currentTeam) return;

    try {
      setIsLoading(true);
      setError(null);

      if (activeTab === 'inbox' && currentShift) {
        // Get inbox for current role
        const result = await api.getInbox(currentTeam.id, currentShift.roleId, true);
        setMessages(result.messages as Message[]);
      } else if (activeTab === 'sent') {
        // Get sent messages
        fetch('/api/debug', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: `Loading sent: teamId=${currentTeam.id}, userId=${user?.teamsUserId}` }) }).catch(() => {});
        const result = await api.getSentMessages(currentTeam.id, user?.teamsUserId || '');
        fetch('/api/debug', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: `Got sent messages: ${result.messages.length}` }) }).catch(() => {});
        setMessages(result.messages as Message[]);
      } else {
        setMessages([]);
      }

      // Load available roles for compose
      const directory = await api.getRoleDirectory(currentTeam.id);
      setRoles(directory.roles.map((r: any) => ({
        id: r.roleId,
        name: r.roleName,
        status: r.status,
      })));
    } catch (err: any) {
      console.error('Failed to load messages:', err);
      setError(err.message || 'Failed to load messages');
    } finally {
      setIsLoading(false);
    }
  }, [currentTeam, currentShift, activeTab, user?.teamsUserId]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Handle message selection
  const handleSelectMessage = async (message: Message) => {
    setSelectedMessage(message);

    // Mark as read if unread and in inbox
    if (activeTab === 'inbox' && !message.readAt && currentTeam) {
      try {
        await api.markAsRead(currentTeam.id, message.id);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === message.id ? { ...m, status: 'read', readAt: new Date().toISOString() } : m
          )
        );
      } catch (err) {
        console.error('Failed to mark as read:', err);
      }
    }
  };

  // Handle send message
  const handleSendMessage = async () => {
    if (!currentTeam || !composeToRole || !composeSubject || !composeBody) return;

    try {
      setIsSending(true);
      setError(null);

      await api.sendMessage({
        teamId: currentTeam.id,
        toRoleId: composeToRole,
        subject: composeSubject,
        body: composeBody,
        priority: composePriority,
        fromUserId: user?.teamsUserId || '',
        fromUserName: user?.displayName || '',
      });

      // Reset form and close
      setComposeOpen(false);
      setComposeToRole('');
      setComposeSubject('');
      setComposeBody('');
      setComposePriority('routine');

      // Refresh messages
      loadMessages();
    } catch (err: any) {
      console.error('Failed to send message:', err);
      setError(err.message || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  // Handle acknowledge handover
  const handleAcknowledgeHandover = async (messageId: string) => {
    if (!currentTeam) return;

    try {
      await api.acknowledgeHandover(currentTeam.id, messageId);
      
      // Update local state
      setSelectedMessage((prev) => {
        if (!prev || prev.id !== messageId) return prev;
        return {
          ...prev,
          handoverHistory: prev.handoverHistory.map((h) =>
            !h.acknowledged ? { ...h, acknowledged: true, acknowledgedAt: new Date().toISOString() } : h
          ),
        };
      });
    } catch (err: any) {
      console.error('Failed to acknowledge handover:', err);
      setError(err.message || 'Failed to acknowledge handover');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return styles.priorityCritical;
      case 'urgent':
        return styles.priorityUrgent;
      default:
        return styles.priorityRoutine;
    }
  };

  const unreadCount = messages.filter((m) => !m.readAt).length;

  if (isLoading && messages.length === 0) {
    return <Loading message="Loading messages..." />;
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <Text size={500} weight="semibold">Messages</Text>
          {currentShift && (
            <Text size={200} style={{ marginLeft: '12px', color: tokens.colorNeutralForeground3 }}>
              as {currentShift.roleName}
            </Text>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button
            icon={<ArrowClockwise24Regular />}
            appearance="subtle"
            onClick={loadMessages}
          />
          <Button
            appearance="primary"
            icon={<Send24Regular />}
            onClick={() => setComposeOpen(true)}
            disabled={!currentShift}
          >
            New Message
          </Button>
        </div>
      </div>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {!currentShift ? (
        <div className={styles.emptyState}>
          <Mail24Regular style={{ fontSize: '48px' }} />
          <Text size={400}>Clock on to view messages</Text>
          <Text>You need to be clocked on to a role to send and receive messages.</Text>
        </div>
      ) : (
        <div className={styles.content}>
          {/* Sidebar - Message List */}
          <div className={styles.sidebar}>
          <TabList
  selectedValue={activeTab}
  onTabSelect={(e, data) => {
    fetch('/api/debug', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: `Tab clicked: ${data.value}` }) }).catch(() => {});
    setActiveTab(data.value as 'inbox' | 'sent');
  }}
>
              <Tab value="inbox">
                Inbox
                {unreadCount > 0 && (
                  <Badge appearance="filled" color="danger" size="small" style={{ marginLeft: '8px' }}>
                    {unreadCount}
                  </Badge>
                )}
              </Tab>
              <Tab value="sent">Sent</Tab>
            </TabList>

            <div className={styles.messageList}>
              {messages.length === 0 ? (
                <div className={styles.emptyState}>
                  <Text>No messages</Text>
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={`${styles.messageItem} ${
                      selectedMessage?.id === message.id ? styles.messageItemSelected : ''
                    } ${!message.readAt && activeTab === 'inbox' ? styles.messageItemUnread : ''}`}
                    onClick={() => handleSelectMessage(message)}
                  >
                    <div className={styles.messageHeader}>
                      <span
                        className={`${styles.priorityIndicator} ${getPriorityColor(message.priority)}`}
                      />
                      <Text className={styles.messageSubject} weight={!message.readAt ? 'semibold' : 'regular'}>
                        {message.subject}
                      </Text>
                      {message.handoverHistory.length > 0 && (
                        <Tooltip content="Message has been handed over" relationship="label">
                          <Warning24Regular style={{ fontSize: '16px', color: tokens.colorPaletteYellowForeground2 }} />
                        </Tooltip>
                      )}
                    </div>
                    <Text className={styles.messagePreview}>
                      {message.body.substring(0, 100)}...
                    </Text>
                    <div className={styles.messageMeta}>
                      <Person24Regular style={{ fontSize: '14px' }} />
                      <span>{activeTab === 'inbox' ? message.fromUserName : message.toRoleName}</span>
                      <span>•</span>
                      <span>{formatDistanceToNow(new Date(message.createdAt), { addSuffix: true })}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Detail Panel */}
          <div className={styles.detailPanel}>
            {selectedMessage ? (
              <Card className={styles.detailCard}>
                <div className={styles.detailHeader}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text size={500} weight="semibold">{selectedMessage.subject}</Text>
                    <Badge
                      appearance={selectedMessage.priority === 'critical' ? 'filled' : 'outline'}
                      color={
                        selectedMessage.priority === 'critical' ? 'danger' :
                        selectedMessage.priority === 'urgent' ? 'warning' : 'subtle'
                      }
                    >
                      {selectedMessage.priority}
                    </Badge>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '12px' }}>
                    <Avatar name={selectedMessage.fromUserName} size={32} />
                    <div>
                      <Text weight="semibold">{selectedMessage.fromUserName}</Text>
                      <Text size={200} style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                        To: {selectedMessage.toRoleName}
                      </Text>
                    </div>
                    <Text size={200} style={{ marginLeft: 'auto', color: tokens.colorNeutralForeground3 }}>
                      {format(new Date(selectedMessage.createdAt), 'PPp')}
                    </Text>
                  </div>
                </div>

                <div className={styles.detailBody}>
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{selectedMessage.body}</Text>

                  {/* Handover History */}
                  {selectedMessage.handoverHistory.length > 0 && (
                    <div className={styles.handoverHistory}>
                      <Text weight="semibold" size={300}>
                        <Warning24Regular style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                        Handover History
                      </Text>
                      {selectedMessage.handoverHistory.map((h, i) => (
                        <div key={i} className={styles.handoverItem}>
                          <Clock24Regular style={{ color: tokens.colorNeutralForeground3 }} />
                          <div style={{ flexGrow: 1 }}>
                            <Text size={200}>
                              {h.fromUserName} → {h.toUserName}
                            </Text>
                            <Text size={200} style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                              {format(new Date(h.timestamp), 'PPp')}
                            </Text>
                          </div>
                          {h.acknowledged ? (
                            <Badge appearance="outline" color="success" icon={<Checkmark24Regular />}>
                              Acknowledged
                            </Badge>
                          ) : activeTab === 'inbox' && h.toUserName === user?.displayName ? (
                            <Button
                              size="small"
                              onClick={() => handleAcknowledgeHandover(selectedMessage.id)}
                            >
                              Acknowledge
                            </Button>
                          ) : (
                            <Badge appearance="outline" color="warning">
                              Pending
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className={styles.detailFooter}>
                  {activeTab === 'inbox' && (
                    <Button
                      appearance="primary"
                      icon={<ArrowReply24Regular />}
                      onClick={() => {
                        // Pre-fill reply
                        setComposeToRole(
                          roles.find((r) => r.name === selectedMessage.fromUserName)?.id || ''
                        );
                        setComposeSubject(`Re: ${selectedMessage.subject}`);
                        setComposeOpen(true);
                      }}
                    >
                      Reply
                    </Button>
                  )}
                </div>
              </Card>
            ) : (
              <div className={styles.emptyState}>
                <MailRead24Regular style={{ fontSize: '48px' }} />
                <Text>Select a message to view</Text>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onOpenChange={(e, data) => setComposeOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>New Message</DialogTitle>
            <DialogContent>
              <div className={styles.composeForm}>
                <Field label="To" required>
                  <Dropdown
                    placeholder="Select a role..."
                    value={roles.find((r) => r.id === composeToRole)?.name || ''}
                    onOptionSelect={(e, data) => setComposeToRole(data.optionValue || '')}
                  >
                    {roles
                      .filter((r) => r.id !== currentShift?.roleId) // Can't message yourself
                      .map((role) => (
                        <Option key={role.id} value={role.id} text={role.name}>
                        {role.name}
                        {role.status === 'vacant' && ' (Vacant)'}
                      </Option>
                      ))}
                  </Dropdown>
                </Field>

                <Field label="Priority">
                  <Dropdown
                    value={composePriority.charAt(0).toUpperCase() + composePriority.slice(1)}
                    onOptionSelect={(e, data) => 
                      setComposePriority((data.optionValue as 'routine' | 'urgent' | 'critical') || 'routine')
                    }
                  >
                    <Option value="routine" text="Routine">
                      <div className={styles.priorityOption}>
                        <span className={`${styles.priorityIndicator} ${styles.priorityRoutine}`} />
                        Routine
                      </div>
                    </Option>
                    <Option value="urgent" text="Urgent">
                      <div className={styles.priorityOption}>
                        <span className={`${styles.priorityIndicator} ${styles.priorityUrgent}`} />
                        Urgent
                      </div>
                    </Option>
                    <Option value="critical" text="Critical">
                      <div className={styles.priorityOption}>
                        <span className={`${styles.priorityIndicator} ${styles.priorityCritical}`} />
                        Critical
                      </div>
                    </Option>
                  </Dropdown>
                </Field>

                <Field label="Subject" required>
                  <Input
                    value={composeSubject}
                    onChange={(e, data) => setComposeSubject(data.value)}
                    placeholder="Enter subject..."
                  />
                </Field>

                <Field label="Message" required>
                  <Textarea
                    value={composeBody}
                    onChange={(e, data) => setComposeBody(data.value)}
                    placeholder="Type your message..."
                    rows={6}
                  />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setComposeOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                icon={isSending ? <Spinner size="tiny" /> : <Send24Regular />}
                onClick={handleSendMessage}
                disabled={isSending || !composeToRole || !composeSubject || !composeBody}
              >
                {isSending ? 'Sending...' : 'Send'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
